import { BankCustodian } from "@/admin/types";
import { BankAccountHoldFunctionType } from "@/employer/types";
import { Config } from "@/shared/config";
import { AnonDocumentKey, InvestmentCustodian, TransactionSource } from "../types";

const bankServiceUri = Config.BankServicebaseUri;
const MaxDailyBalanceRecords = 500;

export default {
  get bankV1 () {
    return `${bankServiceUri}/bank/v1`;
  },
  employerSuspension (): string {
    return `${this.bankV1}/employer-suspension`;
  },
  employerSuspensionByPartnerId (partnerId: number): string {
    return `${this.bankV1}/partner/${partnerId}/employer-suspension`;
  },
  employerSuspensionByEmployerId (partnerId: number , employerId: number): string {
    return `${this.bankV1}/partner/${partnerId}/employer-suspension/${employerId}`;
  },
  employerAccounts (employerId) {
    return `${this.bankV1}/employer/${employerId}/accounts`;
  },
  getSavedTransactions (
      pageNumber,
      transactionFilter,
      ofType?: TransactionSource,
      includePending?: boolean,
      pageSize?: number
    ) {
    let path = `${this.bankV1}/transactions/?page=${pageNumber}&transactionFilter=${transactionFilter}`;

    if (ofType) {
      path += `&ofType=${ofType}`;
    }

    if (includePending) {
      path += `&includePending=${includePending}`;
    }

    if (pageSize) {
      path += `&pageSize=${pageSize}`;
    }

    return path;
  },
  getInvestmentHistory () {
    return `${this.bankV1}/investments/history`;
  },
  getInvestmentProfile () {
    return `${this.bankV1}/investments/profile`;
  },
  getPendingContributions () {
    return `${this.bankV1}/transactions/pendingcontributions`;
  },
  getTransactionsForDay (date: string) {
    return `${this.bankV1}/transactions/?startDate=${date}&endDate=${date}&pageSize=${MaxDailyBalanceRecords}`;
  },
  getHsabTransactions () {
    return `${this.bankV1}/hsab-transactions`;
  },
  getHsabTransactionExport () {
    return `${this.bankV1}/hsab-transactions/export`;
  },
  getDailyBalances (startDate: string, endDate: string) {
    return `${this.bankV1}/hsa/dailysummary?startDate=${startDate}&endDate=${endDate}`;
  },
  getCurrentHsaBalance () {
    return `${this.bankV1}/hsa/currentbalance`;
  },
  getBankAccountStatus () {
    return `${this.bankV1}/partner-portal/accountStatus`;
  },
  getFullAccountNumber (id) {
    return `${this.bankV1}/linked-account-management/${id}/full-account-number`;
  },
  getProfile () {
    return `${this.bankV1}/profile`;
  },
  getProfileAdmin () {
    return `${this.bankV1}/profile/admin`;
  },
  getPreferences () {
    return `${this.bankV1}/preferences`;
  },
  getInvestmentAccountSso () {
    return `${this.bankV1}/investments/sso`;
  },
  getInvestmentAccountCreate () {
    return `${this.bankV1}/investments/create`;
  },
  getHsabInvestmentAccountStatus () {
    return `${this.bankV1}/hsab-investments/account-status`;
  },
  getHsabIframe () {
    return `${this.bankV1}/hsab-investments/iframe`;
  },
  getInvestmentBalances () {
    return `${this.bankV1}/investments/balances`;
  },
  getInvestmentDailyBalances (startDate: string, endDate: string, investmentCustodian: InvestmentCustodian) {
    if (!investmentCustodian) {
      investmentCustodian = InvestmentCustodian.None;
    }

    // tslint:disable-next-line:max-line-length
    return `${this.bankV1}/investments/list/${investmentCustodian}?startDate=${startDate}&endDate=${endDate}`;
  },
  getInvestmentDailyBalancesList (startDate: string, endDate: string, investmentCustodian: InvestmentCustodian) {
    if (!investmentCustodian) {
      investmentCustodian = InvestmentCustodian.None;
    }
    return `${this.bankV1}/investments/multiplebalances/${investmentCustodian}?startDate=${startDate}&endDate=${endDate}`;
  },
  getTransactionTotals (forYear: number) {
    return `${this.bankV1}/transactions/totals/${forYear}`;
  },
  instantBalance () {
    return `${this.bankV1}/balances`;
  },
  intradayTransactions () {
    return `${this.bankV1}/transactions/pending`;
  },
  accounts () {
    return `${this.bankV1}/accounts`;
  },
  accountsAdmin () {
    return `${this.bankV1}/accounts/admin`;
  },
  umbLinkedBankAccount (id?: number) {
    return `${this.bankV1}/umb/linkedaccount/bankaccount` + (id ? `/${id}` : "");
  },
  umbLinkedAccountGet () {
    return `${this.bankV1}/umb/linkedaccount/linkedaccounts`;
  },
  umbLinkedAccountVerify (linkedBankAccountId: number) {
    return `${this.bankV1}/umb/linkedAccount/${linkedBankAccountId}/verify`;
  },
  umbLinkedAccountReVerify (linkedBankAccountId: number) {
    return `${this.bankV1}/umb/linkedAccount/${linkedBankAccountId}/submitforreverification`;
  },
  umbLinkedAccountsUpdateDefault () {
    return `${this.bankV1}/umb/linkedAccount/defaults`;
  },
  externalAccountLinkedBankAccountDelete (id: number) {
    return `${this.bankV1}/externalaccount/delete/${id}`;
  },
  externalAccountLinkedBankAccountAdminDelete (partnerid: number, id: number) {
    return `${this.bankV1}/externalaccount/admin-delete/${partnerid}/${id}`;
  },
  externalAccountLinkedBankAccountAdminReplace (partnerid: number, id: number, forUser: string) {
    return `${this.bankV1}/externalaccount/admin-replace/${partnerid}/${id}?forUser=${forUser}`;
  },
  externalAccountLinkedBankAccountAdminResync (forUser: string) {
    return `${this.bankV1}/externalaccount/admin-resync?forUser=${forUser}`;
  },
  externalAccountLinkedBankAccountAdd () {
    return `${this.bankV1}/externalaccount/add`;
  },
  externalAccountLinkedBankAccountUpdate () {
    return `${this.bankV1}/externalaccount/update`;
  },
  externalAccountLinkedAccountGet () {
    return `${this.bankV1}/externalaccount/getall`;
  },
  externalAccountLinkedAccountGetAdmin () {
    return `${this.bankV1}/externalaccount/admin/getall`;
  },
  externalAccountLinkedAccountReSync (forUser: string, forUserPartnerId: number) {
    return `${this.bankV1}/externalaccount/resync?forUser=${forUser}&forUserPartnerId=${forUserPartnerId}`;
  },
  externalAccountCleanupRequest () {
    return `${this.bankV1}/externalaccount/cleanup-request`;
  },
  externalAccountLinkedAccountUpdateDefault () {
    return `${this.bankV1}/externalaccount/defaults`;
  },
  microEntryAccountValidationUrl (accountId: string) {
    return `${this.bankV1}/externalaccount/micro-entry/${accountId}/generateurl`;
  },
  microEntryAccountDelete (accountId: number) {
    return `${this.bankV1}/externalaccount/micro-entry/${accountId}`;
  },
  verifyHsaUser () {
    return `${this.bankV1}/validate/hsauser`;
  },
  contactInformation () {
    return `${this.bankV1}/contactdetail`;
  },
  partnerBankStatus () {
    return `${this.bankV1}/partner-portal/status`;
  },
  partnerBankSingleSignOnConfig (portal: string) {
    return `${this.bankV1}/partner-portal/sso-config?portal=${portal}`;
  },
  reimburse () {
    return `${this.bankV1}/reimbursement/reimburse`;
  },
  pendingReimbursements () {
    return `${this.bankV1}/reimbursement`;
  },
  reimburseAccountsGet () {
    return `${this.bankV1}/reimbursement/linkedaccounts`;
  },
  personalContribution () {
    return `${this.bankV1}/contribution/contribute`;
  },
  priorYearContributionEligibility () {
    return `${this.bankV1}/contribution/can-contribute-prior-year`;
  },
  getSavedContributions () {
    return `${this.bankV1}/contribution`;
  },
  mobileUmbHandoff (action: string, token: string) {
    return `https://user.bendhsa.com/#/umbsso?action=${action}&token=${token}`;
  },
  getTpaList () {
    return `${this.bankV1}/settings/umb/tpaid-list`;
  },
  partnerTpaId (partnerId: number): string {
    return `${this.bankV1}/settings/umb/${partnerId}/partner-tpa`;
  },
  validateEmployerPartner (currentId: number, chosenId: number) {
    return `${this.bankV1}/settings/umb/${currentId}/validatechange/${chosenId}`;
  },
  partnerIndividualEmployerId (partnerId: number): string {
    return `${this.bankV1}/settings/umb/${partnerId}/individual-employerId`;
  },
  requestInvestmentTransfer () {
    return `${this.bankV1}/investments/transfer`;
  },
  saveInvestmentAccountNumber () {
    return `${this.bankV1}/investments/account`;
  },
  saveInvestmentAccountError () {
    return `${this.bankV1}/investments/error`;
  },
  getBalanceThresholds () {
    return `${this.bankV1}/investments/balance-thresholds`;
  },
  getInvestmentProvider () {
    return `${this.bankV1}/investments/investment-provider`;
  },
  getAchContributions (employerId: number, pageNumber: number, pageSize: number): string {
    return `${this.bankV1}/employer/${employerId}/ach-report/${pageNumber}?pageSize=${pageSize}`;
  },
  getAchContributionTotalRecords (employerId: number): string {
    return `${this.bankV1}/employer/${employerId}/ach-reportTotalRecords`;
  },
  getEmployeeDisbursalReportDownload (employerId: number, achReportId: number) {
    return `${this.bankV1}/employer/${employerId}/ach-report/${achReportId}/download-disbursal-report`;
  },
  employerSettings (employerId: number) {
    return `${this.bankV1}/employer/${employerId}/settings/`;
  },
  employerBankSettings (employerId: number) {
    return `${this.bankV1}/employer-setting/${employerId}`;
  },
  employerBankSettingsForCurrentUser () {
    return `${this.bankV1}/employer-setting/`;
  },
  bankAccountBlacklist () {
    return `${this.bankV1}/blacklist/`;
  },
  bankAccountBlacklistDelete (bankAccountBlacklistId: number) {
    return `${this.bankV1}/blacklist/${bankAccountBlacklistId}`;
  },
  getCustomerFormDocuments () {
    return `${this.bankV1}/form-document/customer`;
  },
  resourceFormDocumentsByPartnerId (partnerId: number) {
    return `${this.bankV1}/form-document/partner/${partnerId}`;
  },
  putFormDocumentOptInOrOptOut (partnerId: number, formDocumentId: number) {
    return `${this.bankV1}/form-document/partner/${partnerId}/opt-in-or-opt-out/${formDocumentId}`;
  },
  getUserHsaCustodian () {
    return `${this.bankV1}/user/hsa-custodian`;
  },
  getEmployerCustodian (employerId: number) {
    return `${this.bankV1}/employer-custodian/${employerId}`;
  },
  postToGenerateValidationUrl () {
    return `${this.bankV1}/externalaccount/generateurl`;
  },
  employerTdaPlanId (employerId: number) {
    return `${this.bankV1}/employer/${employerId}/settings/tda-planid`;
  },
  getUserSystemId (partnerId: number): string {
    return `${this.bankV1}/umbopaqueid/${partnerId}`;
  },
  setUserSystemId (partnerId: number): string {
    return `${this.bankV1}/umbopaqueid/${partnerId}`;
  },
  getPartnerInvestmentThreshold (partnerId: number): string {
    return `${this.bankV1}/partner-settings/${partnerId}/investment-threshold`;
  },
  setPartnerInvestmentThreshold (partnerId: number): string {
    return `${this.bankV1}/partner-settings/${partnerId}/investment-threshold`;
  },
  getEmployerInvestmentThreshold (employerId: number) {
    return `${this.bankV1}/employer/${employerId}/settings/investment-threshold`;
  },
  setEmployerInvestmentThreshold (employerId: number) {
    return `${this.bankV1}/employer/${employerId}/settings/investment-threshold`;
  },
  listPendingContributions () {
    return `${this.bankV1}/pending-contributions`;
  },
  pendingContributionById (contributionId: number) {
    return `${this.bankV1}/pending-contribution/${contributionId}`;
  },
  get transactionEditListing () {
    return `${this.bankV1}/transaction-edit/listing`;
  },
  transactionEditTransaction (id: number) {
    return `${this.bankV1}/transaction-edit/${id}`;
  },
  debitCards () {
    return `${this.bankV1}/debit-cards`;
  },
  activateDebitCard () {
    return `${this.bankV1}/debit-cards/activate`;
  },
  deactivateDebitCard () {
    return `${this.bankV1}/debit-cards/deactivate`;
  },
  reissueDebitCard () {
    return `${this.bankV1}/debit-cards/reissue`;
  },
  avidiaAccountStatements (year: number) {
    return `${this.bankV1}/avidia/statements/${year}`;
  },
  hsaBankStatements () {
    return `${this.bankV1}/hsa-bank/documents/statements`;
  },
  hsaBankCombinedStatements () {
    return `${this.bankV1}/hsa-bank/documents/combined-statements`;
  },
  hsaBankTaxDocuments (year: number) {
    return `${this.bankV1}/hsa-bank/documents/tax-documents/${year}`;
  },
  hsaBankTaxDocumentKey (taxDocumentIdentifier: string) {
    return `${this.bankV1}/hsa-bank/documents/location/tax/${taxDocumentIdentifier}`;
  },
  hsaBankStatementKey (fundedAccountStatementIdentifier: string) {
    return `${this.bankV1}/hsa-bank/documents/location/statement/${fundedAccountStatementIdentifier}`;
  },
  hsaBankNotionalStatementKey (notionalAccountStatementIdentifier: string) {
    return `${this.bankV1}/hsa-bank/documents/location/notional-statement/${notionalAccountStatementIdentifier}`;
  },
  hsaBankAnonDocument (documentKey: AnonDocumentKey) {
    return `${this.bankV1}/hsa-bank/documents/file/${documentKey.key}/${documentKey.signature}`;
  },
  getAvidiaPartner (partnerId: number) {
    return `${this.bankV1}/avidia/partner/${partnerId}`;
  },
  saveAvidiaPartner () {
    return `${this.bankV1}/avidia/partner`;
  },
  getPriorYearDate () {
    return `${this.bankV1}/contribution/prior-year-contribution-cutoff-date`;
  },
  partnerCardCustomization (partnerId: number) {
    return `${this.bankV1}/debit-card-customization/partner/${partnerId}`;
  },
  cardCustomization (custodian: BankCustodian) {
    return `${this.bankV1}/debit-card-customization/${custodian}`;
  },
  getCardCustomizations (custodian: BankCustodian) {
    return `${this.bankV1}/debit-card-customization/${custodian}`;
  },
  linkedAccountManagement () {
    return `${this.bankV1}/linked-account-management`;
  },
  pendingTxMgmt (pendingTxId?: number) {
    if (!pendingTxId) {
      return `${this.bankV1}/pending-tx-management`;
    }

    return `${this.bankV1}/pending-tx-management/${pendingTxId}`;
  },
  getUmbAccountUser (emailAddress: string) {
    return `${this.bankV1}/settings/umb/user?email=${encodeURIComponent(emailAddress)}`;
  },
  reopenUmbAccount () {
    return `${this.bankV1}/settings/umb/reopen-account`;
  },
  getInstantTransferFailures (pageNumber: number, pageSize: number, isArchived: boolean) {
    return `${this.bankV1}/instant-transfers/failed?page=${pageNumber}&pageSize=${pageSize}&isArchived=${isArchived}`;
  },
  getInstantTransferFailuresNew () {
    return `${this.bankV1}/instant-transfers/failed`;
  },
  getInstantTransferFailureDetail (transactionId: string) {
    return `${this.bankV1}/instant-transfers/detail/${transactionId}`;
  },
  resoloveInstantTransferFailure (transactionId: string) {
    return `${this.bankV1}/instant-transfers/resolve/${transactionId}`;
  },
  retryInstantTransferFailure (transactionId: string) {
    return `${this.bankV1}/instant-transfers/retry/${transactionId}`;
  },
  getReimburementFailuresNew () {
    return `${this.bankV1}/reimbursement-failures/failed`;
  },
  getReimburementFailures (pageNumber: number, pageSize: number, isArchived: boolean) {
    return `${this.bankV1}/reimbursement-failures/failed?page=${pageNumber}&pageSize=${pageSize}&isArchived=${isArchived}`;
  },
  getReimbursementFailureDetail (reimbursementId: number) {
    return `${this.bankV1}/reimbursement-failures/detail/${reimbursementId}`;
  },
  resoloveReimbursementFailure (reimbursementId: number) {
    return `${this.bankV1}/reimbursement-failures/resolve/${reimbursementId}`;
  },
  getUmbAccountUsers (pageNumber: number, pageSize: number, searchTerm: string) {
    return `${this.bankV1}/umb/users?page=${pageNumber}&pageSize=${pageSize}&searchTerm=${encodeURIComponent(searchTerm)}`;
  },
  getUmbAccountUserById (umbAccountUserId: number) {
    return `${this.bankV1}/umb/users/${umbAccountUserId}`;
  },
  getHsabAccountTypes () {
    return `${this.bankV1}/hsab-accounts`;
  },
  getHsabAccountTypesAdmin () {
    return `${this.bankV1}/hsab-accounts/admin`;
  },
  getNotionalBalances (accountId: number) {
    return `${this.bankV1}/notional-accounts/balances/${accountId}`;
  },
  getHsabAccountDisplayType () {
    return `${this.bankV1}/hsab-accounts/display-type`;
  },
  getHsabAccountDisplayTypeBatch () {
    return `${this.bankV1}/hsab-accounts/display-type-batch`;
  },
  verifyHsabAccount () {
    return `${this.bankV1}/hsab-accounts/verify`;
  },
  getHsabNotionalTransactions (accountId: number) {
    return `${this.bankV1}/notional-accounts/${accountId}/transactions`;
  },
  getHsabNotionalTransactionExport (accountId: number) {
    return `${this.bankV1}/notional-accounts/${accountId}/transactions/export`;
  },
  umbLinkedAccountSearch () {
    return `${this.bankV1}/umb/linkedaccount/search`;
  },
  umbLinkedAccountDelete (umbLinkedAccountId: number, hsaAccountNumber: string) {
    return `${this.bankV1}/umb/linkedaccount/harddelete/${umbLinkedAccountId}/${hsaAccountNumber}`;
  },
  bankUserAccountNumber (username: string) {
    return `${this.bankV1}/bank-users/${username}/account-number`;
  },
  omnibusTransactionCodeUpload () {
    return `${this.bankV1}/rk-transactioncode`;
  },
  omnibusTransactionCodeResultFile (key: string) {
    return `${this.bankV1}/rk-transactioncode/result/${key}`;
  },
  omnibusTransactionCodeExportFile () {
    return `${this.bankV1}/rk-transactioncode/export`;
  },
  omnibusTransactionCodeResultFileExists (key: string) {
    return `${this.bankV1}/rk-transactioncode/result/${key}/exists`;
  },
  accountHolds () {
    return `${this.bankV1}/bankaccount-holds`;
  },
  holdTypes () {
    return `${this.bankV1}/bankaccount-holds/hold-types`;
  },
  saveHolds (partnerId: number, accountId: number) {
    return `${this.bankV1}/bankaccount-holds/${partnerId}/${accountId}`;
  },
  holdHistoryExport (accountId: number) {
    return `${this.bankV1}/bankaccount-holds/${accountId}/history`;
  },
  getCanPerformHoldFunction (holdFunctionType: BankAccountHoldFunctionType) {
    return `${this.bankV1}/consumer-holds/can-perform-function/${holdFunctionType}`;
  },
  achCompanyName (partnerId: number) {
    return `${this.bankV1}/partner-ach-branding/${partnerId}`;
  },
  distributionSchedule () {
    return `${this.bankV1}/distribution-schedules`;
  },
  personalContributionSchedule () {
    return `${this.bankV1}/contribution-schedules`;
  },
  personalContributionScheduleAdmin () {
    return `${this.bankV1}/contribution-schedules/admin`;
  },
  getpersonalContributionSchedule (scheduleId: number) {
    return `${this.bankV1}/contribution-schedules/${scheduleId}`;
  },
  personalContributionScheduleUpdate (scheduleId: number) {
    return `${this.bankV1}/contribution-schedules/${scheduleId}`;
  },
  cancelPersonalContributionSchedule (scheduleId: number) {
    return `${this.bankV1}/contribution-schedules/cancel/${scheduleId}`;
  },
  cancelPersonalContributionScheduleAdmin (scheduleId: number) {
    return `${this.bankV1}/contribution-schedules/admin/cancel/${scheduleId}`;
  },
  futureContributions () {
    return `${this.bankV1}/contribution-schedules/calendars`;
  },
  futureContributionsAdmin () {
    return `${this.bankV1}/contribution-schedules/admin/calendars`;
  },
  proposedPersonalContributionScheduleCalendars (numberOfCalendarEntries: number) {
    return `${this.bankV1}/contribution-schedules/proposed-calendars?numberOfCalendarEntries=${numberOfCalendarEntries}`;
  },
  personalContributionSchedulingEligibility () {
    return `${this.bankV1}/contribution-schedules/eligibility`;
  },
  getPersonalProjectedContributionsForYear () {
    return `${this.bankV1}/contribution-schedules/upcoming-personal-contributions`;
  },
  proposedPersonalContributionScheduleCalendarsByDate (calculateUntilDate: string) {
    return `${this.bankV1}/contribution-schedules/proposed-calendars/by-date?calculateUntilDate=${calculateUntilDate}`;
  },
  getHsabDailyBalances (startDate: string, endDate: string) {
    return `${this.bankV1}/hsa/hsab/daily-balances?startDate=${startDate}&endDate=${endDate}`;
  },
  authorizedSigners () {
    return `${this.bankV1}/authorized-signers`;
  },
  updateAuthorizedSigner (authorizedSignerId: number): string {
    return `${this.bankV1}/authorized-signers/${authorizedSignerId}`;
  },
  getDebitCardEligibityForAuthorizedSigner () {
    return `${this.bankV1}/authorized-signers/debit-card-eligibility`;
  },
  beneficiaries () {
    return `${this.bankV1}/beneficiaries`;
  },
  cpfBlackout () {
    return `${this.bankV1}/cpf-blackout`;
  },
  cpfBlackoutDelete (date: string) {
    return `${this.bankV1}/cpf-blackout/${date}`;
  },
  getPlanYearEnrollmentDetails () {
    return `${this.bankV1}/hsab-enrollment/`;
  },
  setPlanYearEnrollmentReimbursementMethod () {
    return `${this.bankV1}/hsab-enrollment/ReimbursementMethod`;
  },
  enrollmentSettingsUpdate () {
    return `${this.bankV1}/hsab-enrollment/settings`;
  },
  userHsaAccountsSearchBySsn (): string {
    return `${this.bankV1}/admin-hsa-account/search-by-ssn`;
  },
  saveAccountRepoint (): string {
    return `${this.bankV1}/admin-hsa-account/save-account-repoint`;
  },
  getInvestmentRepointRequests (pageNumber: number, pageSize: number): string {
    return `${this.bankV1}/admin-hsa-account/investment-repoint-requests?pageNumber=${pageNumber}&pageSize=${pageSize}`;
  },
  getAllContributionActivity () {
    return `${this.bankV1}/contribution/all`;
  },
  getAllContributionActivityAdmin () {
    return `${this.bankV1}/contribution/admin/all`;
  },
  getUserHsaCustodianByPartner () {
    return `${this.bankV1}/user/hsa-custodian-by-partner`;
  },
};